import type { EventTicketSummary, TicketSettings, TicketType } from '@pulsify/shared/dto'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight, Eye, Plus, ScanLine, Settings2, Ticket } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEvent } from '~/entities/event/model/use-events'
import {
  useDeleteTicketType,
  useEventTicketSummary,
  useEventTicketTypes,
  useSaveTicketType,
  useTicketSettings,
} from '~/entities/ticket/model/use-tickets'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Disclosure } from '~/shared/ui/disclosure'
import { Skeleton } from '~/shared/ui/skeleton'
import type { PayoutReturn } from './payout-return'
import { PayoutSetupPrompt, usePayoutReturn } from './payout-setup'
import { previewTicket } from './ticket-face'
import { formatCents } from './ticket-format'
import { TicketPreview } from './ticket-preview'
import { TicketSettingsPanel } from './ticket-settings-panel'
import { TicketTypeDialog } from './ticket-type-dialog'
import { TicketTypeList } from './ticket-type-list'

export const EVENT_TICKETS_ANCHOR = 'tickets'

export function EventTicketsCard({
  eventId,
  payoutReturn,
}: {
  eventId: string
  payoutReturn?: PayoutReturn
}) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: event } = useEvent(businessId, eventId)
  const types = useEventTicketTypes(businessId, eventId)
  const settings = useTicketSettings(businessId)
  const hasTypes = (types.data?.length ?? 0) > 0
  const summary = useEventTicketSummary(businessId, eventId, { enabled: hasTypes })
  const remove = useDeleteTicketType(businessId, eventId)
  const save = useSaveTicketType(businessId, eventId)
  const [dialog, setDialog] = useState<{ open: boolean; type: TicketType | null }>({
    open: false,
    type: null,
  })
  const navigate = useNavigate()
  usePayoutReturn(businessId, payoutReturn, () =>
    navigate({ to: '/dashboard/events/$eventId', params: { eventId }, search: {}, replace: true }),
  )
  const cardRef = useRef<HTMLDivElement>(null)
  const ready = !!event && !!types.data && !!settings.data

  useEffect(() => {
    if (ready && window.location.hash === `#${EVENT_TICKETS_ANCHOR}`) {
      cardRef.current?.scrollIntoView({ block: 'start' })
    }
  }, [ready])

  if (types.isError || settings.isError) return null
  if (!event || !types.data || !settings.data) {
    return <Skeleton className="h-28 w-full rounded-xl" />
  }

  const openDialog = (type: TicketType | null) => setDialog({ open: true, type })
  const sampleType = types.data.find((type) => type.active) ?? types.data[0] ?? null
  const preview = (
    <Disclosure
      title={m.ticketPreviewTitle()}
      hint={m.ticketPreviewHint()}
      icon={<Eye />}
      contentClassName="pb-3"
    >
      <TicketPreview ticket={previewTicket(event, sampleType)} />
    </Disclosure>
  )
  const deleteType = (type: TicketType) =>
    remove.mutate(type.id, {
      onSuccess: () => toast.success(m.ticketsDeleted()),
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })

  return (
    <Card ref={cardRef} id={EVENT_TICKETS_ANCHOR} className="scroll-mt-6 space-y-3 p-4 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-semibold font-serif text-lg">
          <Ticket className="size-5" />
          {m.ticketsTitle()}
        </h2>
        {hasTypes ? (
          <Button variant="outline" size="sm" onClick={() => openDialog(null)}>
            <Plus />
            {m.ticketsAddType()}
          </Button>
        ) : null}
      </div>

      {hasTypes ? (
        <TicketsOverview
          businessId={businessId}
          eventId={eventId}
          types={types.data}
          settings={settings.data}
          summary={summary.data}
          liveRoomOn={event.liveRoomMode !== 'off'}
          onEdit={openDialog}
          onDelete={deleteType}
          preview={preview}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 flex-1 text-muted-foreground text-sm">{m.ticketsEmpty()}</p>
            <Button size="sm" onClick={() => openDialog(null)}>
              <Plus />
              {m.ticketsAddFirst()}
            </Button>
          </div>
          <div className="border-border border-t">{preview}</div>
        </>
      )}

      <TicketTypeDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((current) => ({ ...current, open }))}
        type={dialog.type}
        settings={settings.data}
        onSave={async (input) => {
          await save.mutateAsync({ typeId: dialog.type?.id ?? null, input })
          toast.success(m.ticketsSaved())
        }}
      />
    </Card>
  )
}

function TicketsOverview({
  businessId,
  eventId,
  types,
  settings,
  summary,
  liveRoomOn,
  onEdit,
  onDelete,
  preview,
}: {
  businessId: string
  eventId: string
  types: TicketType[]
  settings: TicketSettings
  summary: EventTicketSummary | undefined
  liveRoomOn: boolean
  onEdit: (type: TicketType) => void
  onDelete: (type: TicketType) => void
  preview: ReactNode
}) {
  const sellsPaid = types.some((type) => type.active && type.priceCents > 0)
  const cap = sellsPaid ? settings.firstSalesCap : null

  return (
    <>
      <TicketTypeList types={types} onEdit={onEdit} onDelete={onDelete} />
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted px-3 py-2.5">
        <div className="min-w-0 flex-1 text-sm tabular-nums">
          {summary ? salesLine(summary) : <Skeleton className="h-4 w-40" />}
        </div>
        <Button asChild size="sm">
          <Link to="/dashboard/events/$eventId/tickets" params={{ eventId }}>
            {m.ticketsAllBuyers()}
            <ArrowRight />
          </Link>
        </Button>
      </div>
      {sellsPaid && !settings.connect.onboardingComplete ? (
        <PayoutSetupPrompt businessId={businessId} settings={settings} />
      ) : null}
      {cap ? (
        cap.usedCents >= cap.limitCents ? (
          <p className="text-sm">{m.ticketsCapReached({ limit: formatCents(cap.limitCents) })}</p>
        ) : (
          <p className="text-muted-foreground text-sm">
            {m.ticketsCapLine({
              limit: formatCents(cap.limitCents),
              used: formatCents(cap.usedCents),
            })}
          </p>
        )
      ) : null}
      {liveRoomOn ? (
        <p className="flex items-start gap-2 text-muted-foreground text-sm">
          <ScanLine aria-hidden className="mt-0.5 size-4 shrink-0" />
          {m.ticketsLiveRoomNote()}
        </p>
      ) : null}
      <div className="divide-y divide-border border-border border-t">
        <Disclosure
          title={m.ticketsSettings()}
          hint={
            settings.feeMode === 'buyer'
              ? m.ticketsSettingsHintBuyer()
              : m.ticketsSettingsHintOrganizer()
          }
          icon={<Settings2 />}
          contentClassName="pb-3"
        >
          <TicketSettingsPanel businessId={businessId} settings={settings} />
        </Disclosure>
        {preview}
      </div>
    </>
  )
}

function salesLine(summary: EventTicketSummary): string {
  const tickets = String(summary.issued)
  return summary.sellsPaid
    ? m.ticketsSalesHintMoney({ tickets, gross: formatCents(summary.grossCents) })
    : m.ticketsSalesHint({ tickets, checkedIn: String(summary.checkedIn) })
}
